import { GetTokenApi } from "./GetTokenApi";
import type { TokenRequestParams, EnterpriseTokenRequestParams, ConsumerTokenRequestParams, } from "owa-tokenprovider-utils";
import { getTokenFromMsal } from "./utils/getTokenFromMsal";
import { LogStartPerformanceUsage } from "owa-analytics-start";
import type { TokenResponseExtended as TokenResponse } from "owa-service/lib/tokenResponseExtended";
import type TokenResponseCode from "owa-service/lib/contract/TokenResponseCode";
import { isBootFeatureEnabled } from "owa-metatags";
import type { MailboxInfo } from "owa-client-types";
import type { TokenPerformanceLogger } from "./utils/TokenPerformanceLogger";

/*
    GetTokenApiUsingMSAL: used to fetch token from OWA in MSAL,
    Uses GetTokenApi as a fallback to fetch token from Server
*/
export class GetTokenApiUsingMSAL extends GetTokenApi {
    public readonly LoggerPrefix = "GetTokenApiUsingMSAL";
    
    protected override createDatapoint(mailboxInfo: MailboxInfo | undefined): TokenPerformanceLogger {
        return new LogStartPerformanceUsage("GetTokenApiUsingMSAL", mailboxInfo, {
            sessionSampleRate: 2,
        });
    }
    
    public async getTokenForEnterpriseCallback(params: TokenRequestParams, perfLog: TokenPerformanceLogger): Promise<TokenResponse> {
        const enterpriseParams = params as EnterpriseTokenRequestParams;
        
        let cacheKey: string = "";
        if (isBootFeatureEnabled("auth-unifiedCache")) {
            if (enterpriseParams.mailboxInfo) {
                cacheKey = this.unifiedCache.getCacheKey(enterpriseParams.mailboxInfo, enterpriseParams.resource, enterpriseParams.scope, enterpriseParams.targetTenantId, undefined /* childAppId */);
            }
        }
        else {
            cacheKey = this.tokenCache.getCacheKey(enterpriseParams);
        }
        
        perfLog.addCustomProperty(`${this.LoggerPrefix}EnterpriseCallback_FetchingTokenFromMSAL`, true);
        const tokenResponse = await getTokenFromMsal(enterpriseParams, perfLog);
        
        if (tokenResponse &&
            tokenResponse.TokenResultCode === 0 &&
            tokenResponse.AccessToken) {
            perfLog.addCustomProperty(`${this.LoggerPrefix}EnterpriseCallback_FetchedFromMSAL`, true);
            
            // only cache the response if we're not going to fallback to getTokenForResource
            if (isBootFeatureEnabled("auth-unifiedCache")) {
                if (enterpriseParams.mailboxInfo) {
                    this.unifiedCache.putCachedToken(cacheKey, false, tokenResponse, () => this.getTokenForEnterpriseCallback(enterpriseParams, perfLog));
                }
            }
            else {
                this.tokenCache.putCachedToken(cacheKey, enterpriseParams, tokenResponse, () => this.getTokenForEnterpriseCallback(enterpriseParams, perfLog));
            }
            return tokenResponse;
        }
        
        // Preserve the sub-error details surfaced by MSAL so they bubble up to
        // callers instead of being lost on the failure path.
        return {
            TokenResultCode: 2,
            SubErrorCode: tokenResponse?.SubErrorCode,
            SubError: tokenResponse?.SubError,
            ErrorMessage: tokenResponse?.ErrorMessage,
        };
    }
    
    public async getTokenForConsumerCallback(params: TokenRequestParams, perfLog: TokenPerformanceLogger): Promise<TokenResponse> {
        const consumerParams = params as ConsumerTokenRequestParams;
        
        let cacheKey: string = "";
        if (isBootFeatureEnabled("auth-unifiedCache")) {
            if (consumerParams.mailboxInfo) {
                cacheKey = this.unifiedCache.getCacheKey(consumerParams.mailboxInfo, consumerParams.resource, consumerParams.scope, consumerParams.targetTenantId, undefined /* childAppId */);
            }
        }
        else {
            cacheKey = this.tokenCache.getCacheKey(consumerParams);
        }
        
        perfLog.addCustomProperty(`${this.LoggerPrefix}ConsumerCallback_FetchingTokenFromMSAL`, true);
        const tokenResponse = await getTokenFromMsal(consumerParams, perfLog);
        
        if (tokenResponse) {
            perfLog.addCustomProperty(`${this.LoggerPrefix}ConsumerCallback_FetchedFromMSAL`, tokenResponse.TokenResultCode === 0 &&
                !!tokenResponse.AccessToken);
            
            if (isBootFeatureEnabled("auth-unifiedCache")) {
                if (consumerParams.mailboxInfo) {
                    this.unifiedCache.putCachedToken(cacheKey, false, tokenResponse, () => this.getTokenForConsumerCallback(consumerParams, perfLog));
                }
            }
            else {
                this.tokenCache.putCachedToken(cacheKey, consumerParams, tokenResponse, () => this.getTokenForConsumerCallback(consumerParams, perfLog));
            }
            return tokenResponse;
        }
        
        return { TokenResultCode: 2 };
    }
}
